// Small pure helpers extracted out of VisitorRegistrationModal so the validation logic they
// encode can be unit-tested without mounting the (very large, dependency-heavy) modal itself.

/**
 * Whether the "Período de Acesso Personalizado" fields should be validated on submit.
 *
 * "Visitante Sempre Habilitado" (visitor only) takes precedence and hides the custom-period
 * section entirely (see the render gate in VisitorRegistrationModal). If a user toggles
 * useCustomPeriodAccess on, then switches to "Sempre Habilitado" without the custom-period
 * toggle ever getting reset, `useCustomPeriodAccess` can be stale-true while the section is no
 * longer visible — validating it in that state would block submission with an error for a
 * field the user can no longer see or fill in.
 */
export function isCustomPeriodValidationRequired(
  roleType: string,
  isAlwaysEnabled: boolean,
  useCustomPeriodAccess: boolean,
): boolean {
  if (roleType === 'visitor' && isAlwaysEnabled) return false;
  return useCustomPeriodAccess;
}

/** The identity fields "Cadastrar Acesso" lets an operator correct on an existing person. */
export interface PersonIdentityDraft {
  /** Single "Nome Completo" field, as typed. */
  fullName: string;
  /** CPF as typed, masked or not. */
  cpf: string;
  /** Telefone as typed, masked or not. Omitted/blank means "not filled in". */
  phone?: string;
  /** RG as typed. Omitted/blank means "not filled in". */
  rg?: string;
  /** Data de Nascimento as typed, DD/MM/AAAA. Anything shorter is still being typed. */
  birthDate?: string;
  /** "Menor de 18 anos". Only ever sent as true — see buildPersonIdentityUpdate. */
  isMinor?: boolean;
  /** Custom field values keyed by field id, as typed. Blank values are "not filled in". */
  extraData?: Record<string, any>;
}

export interface ExistingPersonIdentity {
  first_name?: string;
  last_name?: string;
  cpf?: string | null;
  phone?: string | null;
  rg?: string | null;
  birth_date?: string | null;
  is_minor?: boolean | null;
  extra_data?: Record<string, any> | null;
}

export interface PersonIdentityUpdate {
  first_name?: string;
  last_name?: string;
  cpf?: string;
  phone?: string;
  rg?: string;
  birth_date?: string;
  is_minor?: boolean;
  extra_data?: Record<string, any>;
}

/** Shortest dialable Brazilian number: 2-digit area code + 8-digit landline. */
const MIN_PHONE_DIGITS = 10;

const digitsOnly = (value: string): string => (value || '').replace(/\D/g, '');
const collapseSpaces = (value: string): string => (value || '').trim().replace(/\s+/g, ' ');

/**
 * What to send when the operator edits the name/CPF of an ALREADY REGISTERED person while
 * granting them a new visit.
 *
 * Returns null when nothing meaningful changed, so the common case (just register the visit) adds
 * no write at all. Throws on input that would damage the record — the caller surfaces the message.
 *
 * Two deliberate asymmetries:
 *   - A BLANK CPF field is "I did not fill this in", never "erase the stored CPF". This screen
 *     exists to register a visit; silently destroying identity data from it would be indefensible.
 *   - A blank or single-word NAME is rejected rather than ignored, because the field is prefilled:
 *     an operator who emptied it is mid-edit, and saving the old value behind their back would be
 *     just as surprising as saving an empty one.
 */
export function buildPersonIdentityUpdate(
  existing: ExistingPersonIdentity,
  draft: PersonIdentityDraft,
): PersonIdentityUpdate | null {
  const update: PersonIdentityUpdate = {};

  const typedName = collapseSpaces(draft.fullName);
  if (!typedName) {
    throw new Error('Informe o nome completo');
  }
  const parts = typedName.split(' ');
  if (parts.length < 2) {
    throw new Error('Informe nome e sobrenome');
  }

  const currentName = collapseSpaces(`${existing.first_name || ''} ${existing.last_name || ''}`);
  if (typedName.toLocaleLowerCase('pt-BR') !== currentName.toLocaleLowerCase('pt-BR')) {
    update.first_name = parts[0];
    update.last_name = parts.slice(1).join(' ');
  }

  const typedCPF = digitsOnly(draft.cpf);
  if (typedCPF) {
    if (typedCPF.length !== 11) {
      throw new Error('CPF deve ter 11 dígitos');
    }
    if (typedCPF !== digitsOnly(existing.cpf || '')) {
      update.cpf = typedCPF;
    }
  }

  // Phone follows the same rule as CPF: a blank field is "I did not fill this in", never
  // "erase the stored number". Losing a visitor's phone from a visit-registration screen would
  // be indefensible — it is how the porteiro reaches them.
  //
  // Compared digits-only so merely reformatting the same number is not treated as a change,
  // but stored exactly as typed, matching what the create path sends.
  const typedPhoneDigits = digitsOnly(draft.phone || '');
  if (typedPhoneDigits) {
    if (typedPhoneDigits.length < MIN_PHONE_DIGITS) {
      throw new Error('Telefone incompleto — informe DDD e número');
    }
    if (typedPhoneDigits !== digitsOnly(existing.phone || '')) {
      update.phone = (draft.phone || '').trim();
    }
  }

  // The rest of the condomínio's registration configuration — RG, Data de Nascimento and the
  // custom fields. Same rule throughout: blank is "not filled in", never an erasure.
  const typedRG = (draft.rg || '').trim();
  if (typedRG && typedRG !== (existing.rg || '').trim()) {
    update.rg = typedRG;
  }

  const typedBirthDate = toISOInstant(draft.birthDate);
  if (typedBirthDate && typedBirthDate !== toISOInstant(isoToTypedDate(existing.birth_date))) {
    update.birth_date = typedBirthDate;
    // is_minor is only ever sent ALONGSIDE the date it describes, and only when true. Nothing
    // prefills the checkbox, so an untouched form reads false for a stored minor — sending that
    // would clear the flag on a child as a side effect of registering their visit.
    if (draft.isMinor) {
      update.is_minor = true;
    }
  }

  // extra_data is ONE JSONB column holding every custom field, so this merges rather than
  // replaces: sending only the fields this form knows about would drop values written elsewhere,
  // or belonging to a field the admin has since disabled.
  const typedExtras = Object.entries(draft.extraData || {})
    .filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '');
  if (typedExtras.length > 0) {
    const existingExtras = existing.extra_data || {};
    const merged = { ...existingExtras };
    let changed = false;
    for (const [key, value] of typedExtras) {
      if (merged[key] !== value) {
        merged[key] = value;
        changed = true;
      }
    }
    if (changed) {
      update.extra_data = merged;
    }
  }

  return Object.keys(update).length > 0 ? update : null;
}

/** "09/03/1986" → "1986-03-09T00:00:00Z". Anything not fully typed yet → undefined. */
function toISOInstant(typed: string | undefined | null): string | undefined {
  const value = (typed || '').trim();
  if (value.length !== 10) return undefined;
  const [dd, mm, yyyy] = value.split('/');
  if (!dd || !mm || !yyyy || yyyy.length !== 4) return undefined;
  return `${yyyy}-${mm}-${dd}T00:00:00Z`;
}

/** "1986-03-09T00:00:00Z" → "09/03/1986", so a stored date can be compared with a typed one. */
function isoToTypedDate(stored: string | undefined | null): string | undefined {
  const value = (stored || '').trim();
  if (value.length < 10) return undefined;
  const [yyyy, mm, dd] = value.slice(0, 10).split('-');
  if (!yyyy || !mm || !dd) return undefined;
  return `${dd}/${mm}/${yyyy}`;
}

export interface PreRegistrationRoleInput {
  /** 'visitor' | 'service_provider'. Defaults to visitor. */
  roleType?: string;
  /** Selected unit, when one was chosen. `label` is "Bloco A - 101" or just "Casa 12". */
  unit?: { value: string; label?: string };
  block?: string;
  /** Selected responsible resident, when one was chosen. */
  responsible?: { value: string; label?: string };
}

export interface PreRegistrationRolePayload {
  role_type: string;
  valid_from: string;
  is_authorized: boolean;
  role_metadata: Record<string, any>;
}

/** "Ana Souza - 101" → "Ana Souza". */
const authorizedByName = (label?: string): string => (label || '').split(' - ')[0].trim();

/**
 * The person_role written by a pré-cadastro.
 *
 * The role used to be created only when a unit had been selected. Without one, the person row
 * was written and no role with it — and every list of visitors does
 * `INNER JOIN person_roles ... WHERE role_type = ?`, so a person holding zero roles is
 * unreachable from the UI entirely. The operator saw "Pré-cadastro realizado!" for somebody who
 * then could not be found, searched for, or granted access.
 *
 * So the role is always created, and the unit is treated for what it is: metadata that may not
 * be known yet, attached later when access is actually released.
 *
 * is_authorized stays false and visitor_state stays 'pre_registered' in both cases — a
 * pre-registration is not an active visit and must not open the 15-minute entry window.
 */
export function buildPreRegistrationRolePayload(
  input: PreRegistrationRoleInput,
): PreRegistrationRolePayload {
  const metadata: Record<string, any> = { visitor_state: 'pre_registered' };

  if (input.unit?.value) {
    const label = input.unit.label || '';
    metadata.unit_id = input.unit.value;
    metadata.unit = label.includes(' - ') ? label.split(' - ')[1] : label;
    metadata.unit_display = label;
  }
  if (input.block) {
    metadata.block = input.block;
  }
  if (input.responsible?.value) {
    metadata.authorized_by_resident_id = input.responsible.value;
    metadata.authorized_by_name = authorizedByName(input.responsible.label);
  }

  return {
    role_type: input.roleType || 'visitor',
    valid_from: new Date().toISOString(),
    is_authorized: false,
    role_metadata: metadata,
  };
}

/** A unit as the resident picker knows it, carrying the block it belongs to. */
export interface SelectableUnit {
  value: string;
  label: string;
  blockId?: string;
  blockName?: string;
}

/**
 * Which unit should be selected after the operator picks the morador responsável.
 *
 * Picking the resident used to overwrite the unit with THEIR first one unconditionally. An
 * operator who had already filtered to "UNIDADES – 148" and then chose a resident who also holds
 * 102 watched the field jump to 102 — and, unless they noticed, registered the visit against the
 * wrong unit. A resident with several units is exactly when this matters and exactly when it
 * broke.
 *
 * So: keep what the operator chose when the resident actually holds it. Replace it otherwise —
 * a unit they have no role in would be an unauthorised liberação — and fall back to their first
 * unit when nothing was chosen yet.
 */
export function resolveUnitAfterResidentSelection(
  currentUnitId: string | null | undefined,
  availableUnits: SelectableUnit[]
): SelectableUnit | null {
  if (availableUnits.length === 0) return null;
  if (currentUnitId) {
    const kept = availableUnits.find((u) => u.value === currentUnitId);
    if (kept) return kept;
  }
  return availableUnits[0];
}

/**
 * Whether a filter select should pick its only option for the operator.
 *
 * A condomínio with a single quadra/bloco makes that select a formality — one option, one click,
 * every single time. [alreadyAutoSelected] is what keeps it from fighting the user: the field is
 * clearable, and without the latch the effect would re-select the moment it was emptied.
 */
export function shouldAutoSelectSingleOption<T>(
  options: T[],
  current: T | null | undefined,
  alreadyAutoSelected: boolean
): boolean {
  if (alreadyAutoSelected) return false;
  if (current) return false;
  return options.length === 1;
}

/** A unit as the modal's loaded unit list knows it. */
export interface LoadedUnit {
  id: string;
  number?: string;
  name?: string;
  block?: { id?: string; name?: string };
}

/**
 * Every unit a resident actually holds, in the shape the unit select needs.
 *
 * A resident role records its PRIMARY unit in `unit_info` and the full set in
 * `unit_assignments[]` (older roles use `unit_ids[]` / `unit_id`). Reading only `unit_info` — as
 * this modal did — makes a multi-unit resident look like a one-unit resident: filter to 148, pick
 * the morador, and the modal "corrects" the unit to 102 because 102 was the only one it knew.
 *
 * The primary comes first, so it stays the fallback when the operator has not chosen a unit yet.
 * [lookupUnit] resolves a unit id against the list the modal already loaded (for the block the
 * select needs); assignments pointing outside that list fall back to the names they carry.
 */
export function collectResidentUnits(
  roles: any[] | undefined | null,
  lookupUnit: (unitId: string) => LoadedUnit | undefined
): SelectableUnit[] {
  const byId = new Map<string, SelectableUnit>();

  const add = (unitId: string | undefined, fallbackUnitName?: string, fallbackBlockName?: string) => {
    if (!unitId || byId.has(unitId)) return;
    const loaded = lookupUnit(unitId);
    const blockName = loaded?.block?.name || fallbackBlockName || '';
    const unitName = loaded?.number || loaded?.name || fallbackUnitName || '';
    // Nothing to put in front of the operator — an option with an empty label is worse than one
    // fewer option.
    if (!blockName && !unitName) return;
    byId.set(unitId, {
      value: unitId,
      label: blockName && unitName ? `${blockName} - ${unitName}` : blockName || unitName,
      blockId: loaded?.block?.id,
      blockName: blockName || undefined,
    });
  };

  for (const role of (roles || []).filter((r: any) => r?.role_type === 'resident')) {
    const meta = role?.role_metadata || {};

    // Primary first.
    const info = meta.unit_info;
    if (info?.id) add(info.id, info.number || info.name, info.block?.name);

    for (const assignment of Array.isArray(meta.unit_assignments) ? meta.unit_assignments : []) {
      add(assignment?.unit_id, assignment?.unit_number || assignment?.number, assignment?.block_name);
    }

    // Legacy shapes, still present on roles created before unit_assignments existed.
    for (const legacyId of Array.isArray(meta.unit_ids) ? meta.unit_ids : []) {
      add(legacyId);
    }
    if (meta.unit_id) add(meta.unit_id);
  }

  return Array.from(byId.values());
}
